/**
 * GI38 기타출고 상수.
 *
 * 센터명(WH_NM)·다운로드 사유코드(DOWNLOAD_CD)는 GET /api/v1/combos 응답을 쓴다(LOGISPRD-12385) —
 * 화면 진입 시 `@/services/combo/useComboQueries.useGetCombos` 로 조회해 컴포넌트에 주입한다.
 * 배송유형(TRANSPORT_TYPE_OPTIONS)은 MOMS_COMBO_INFO 가 아니라 moms-be `TransportType` enum 이
 * 정본이라(아래 주석) 화면 상수로 남긴다.
 */

/**
 * 메뉴코드. 조회는 `_HEAD` 접미가 붙는다.
 *
 * 2.0 신규 메뉴는 GI38 이지만 API 에 싣는 값은 1.0 GI35 를 유지한다 — BE 가 이 값을 되돌려주기만 하고
 * 조회 키로 쓰지 않으며(`OutboundEtcController` DEFAULT_MENU_CODE='GI35'), 1.0 과 같은 값이어야
 * 서버 감사 기록이 한 화면으로 이어진다.
 */
export const ETC_OUTBOUND_MENU_CD = 'GI35'
export const ETC_OUTBOUND_SEARCH_MENU_CD = 'GI35_HEAD'

/** 조회 실패 폴백 문구 — BE `error()` 가 문구를 비웠을 때. */
export const ETC_OUTBOUND_SEARCH_FAILED_MESSAGE = '조회 중 오류가 발생했습니다.'

/** 조회 요청 구분(type) — 1.0 은 조회 버튼이 'click', 페이지 이동이 'pagination' 이었다. 서버는 되돌려주기만 한다. */
export const ETC_OUTBOUND_SEARCH_TYPE = { first: 'click', next: 'pagination' } as const

/** 조회 한 번 크기 — BE `MAX_QUERY_PAGE_SIZE`(10,000, 넘으면 400). 전량은 이 크기로 끊어 받는다(LOGISPRD-15268). */
export const ETC_OUTBOUND_FETCH_SIZE = 10000

/** 1회 저장 최대 건수 — BE `ProcessEtcOutboundInnerData.MAX_ITEMS_SIZE`. */
export const ETC_OUTBOUND_MAX_SAVE_SIZE = 5000

/** 1회 확정 최대 건수 — BE `EtcOutboundConfirmRequest.MAX_CONFIRM_SIZE`. */
export const ETC_OUTBOUND_MAX_CONFIRM_SIZE = 5000

/** SKU 미해소 차단 식별자 — 저장·확정이 같은 값을 쓴다(BE `ERROR_SKU_UNMAPPED`). */
export const ERROR_OUTBOUND_SKU_UNMAPPED = 'ERROR_OUTBOUND_SKU_UNMAPPED'

/** 저장 진행률 폴링 간격(ms) — 1.0 grid.js:614 와 같다. */
export const PROGRESS_POLLING_INTERVAL = 10000

/**
 * 진행률 조회 연속 실패 상한 — 1.0 progressPollingEpic 의 `errorCount >= 5` 와 같다.
 * 이 횟수를 넘기면 폴링을 멈춘다. 없으면 엔드포인트가 계속 죽어 있을 때 요청이 무한히 나간다.
 */
export const PROGRESS_MAX_FAILURES = 5

/** 진행률 상태값. 종료 판정이 이 두 값을 문자열 리터럴로 흩어 쓰지 않게 한 곳에 둔다. */
export const PROGRESS_COMPLETED = 'COMPLETED'
export const PROGRESS_FAILED = 'FAILED'

/** 진행률 종료 상태 — 이 값이 오면 폴링을 멈춘다. */
export const PROGRESS_TERMINAL_STATUSES = [PROGRESS_COMPLETED, PROGRESS_FAILED]

/**
 * 진행률 조회 시도 상한 — 10초 × 60 = 10분.
 *
 * 다른 종료 조건이 전부 못 막는 경우가 있다. 워커가 죽어 진행률이 RUNNING 인 채 200 을 계속
 * 돌려주면 상태도 안 끝나고 100% 에도 안 닿고 실패 카운트도 안 오른다. 정상 응답이라서다.
 * 1.0 은 사람이 모달을 닫아 끝났지만 2.0 은 스스로 끊어야 한다.
 */
export const PROGRESS_MAX_ATTEMPTS = 60

/**
 * 화주유형 — BE `LogisticsServiceType` enum 6종. 데모 SHIPPER_TYPE_OPTIONS 와 일치한다.
 */
export const LOGISTICS_SERVICE_TYPE_OPTIONS = [
  { value: 'M1P', label: 'M1P' },
  { value: 'MFS', label: 'MFS' },
  { value: 'MWP', label: 'MWP' },
  { value: 'MLT', label: 'MLT' },
  { value: 'MCD', label: 'MCD' },
  { value: 'MTP', label: 'MTP' },
]

/**
 * 배송유형 — BE `TransportType` enum 9종.
 *
 * 1.0 콤보(MOMS_COMBO_INFO TRS_TYPE)는 12종이라 데모도 12종을 보여주지만 그대로 옮기면 안 된다.
 * BE `TransportType.fromCode` 는 목록에 없는 값을 조용히 '택배'로 떨어뜨려서, 콤보에만 있는
 * 긴급(퀵)·수출·택배-특송·기타로 조회하면 엉뚱하게 택배 결과가 나온다. 조회 응답도 이 enum 의
 * 코드만 내려오므로 표에 뜨는 값 역시 9종뿐이다. 데이터 계약은 BE 실측이 정본이다(결정 02 §4).
 */
export const TRANSPORT_TYPE_OPTIONS = [
  { value: '새벽배송', label: '새벽배송' },
  { value: '당일배송', label: '당일배송' },
  { value: '익일배송', label: '익일배송' },
  { value: '택배', label: '택배' },
  { value: '택배-명품', label: '택배-명품' },
  { value: '택배-단포', label: '택배-단포' },
  { value: '택배-현지', label: '택배-현지' },
  { value: '용차', label: '용차' },
  { value: '특송', label: '특송' },
]

/** 인터페이스 구분 — BE 는 boolean 이고 화면 표기는 Y/N 이다. */
export const IF_REQ_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
]

/**
 * 기타출고를 지원하는 화주 — BE `EtcOutboundValidator.validateShipperCode` 가 조회·저장·확정
 * 세 곳 모두에서 이 목록 밖 화주를 400 으로 막는다(`ETC_OUTBOUND_AVAILABLE_SHIPPERS`).
 *
 * 29CM 은 피처 플래그(`feature.etcOutbound29cmEnabled`) ON 일 때만 허용된다
 * (`ETC_OUTBOUND_FF_SHIPPERS`, LOGISPRD-9941). 플래그 상태를 화면에서 알 길이 없어 목록에는
 * 넣어 두고, 꺼져 있으면 BE 안내 문구가 그대로 뜬다.
 */
export const ETC_OUTBOUND_AVAILABLE_SHIPPERS = ['MUSINSA', 'NIKE', 'NIKESAMPLE', '29CM_NIKE', '29CM']

/** 기타출고 엑셀 다운로드(비밀번호 zip, LOGISPRD-15805) — `useExcelDownload` 로 부른다. 바디는 `EtcOutboundDownloadParams`. */
export const ETC_OUTBOUND_DOWNLOAD_URL = '/api/v1/get-etc-outbounds/download'
